import { brandSelectionLabel } from "../../lib/brand-identity";
import { useLocation, useNavigate } from "react-router-dom";
import { useActiveBrand } from "../../hooks/useActiveBrand";
import { buildBrandScopedPath } from "../../lib/brand-navigation";
import { Card } from "../ui/Card";
import { LoadErrorState, LoadingState } from "../ui/LoadState";

type OperatorBrandGateProps = {
  title: string;
  description: string;
};

export function OperatorBrandGate({ title, description }: OperatorBrandGateProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { brands, activeBrandId, setActiveBrandId, hasMultipleBrands, isLoading, isLoadingError, refetch } = useActiveBrand();

  function activateBrand(brandId: string) {
    setActiveBrandId(brandId);
    navigate(buildBrandScopedPath(`${location.pathname}${location.search}`, brandId), { replace: true });
  }

  // While brands load, or when loading them failed, we do not know which brands
  // exist — never claim "No accessible brands" (UI-PRINCIPLES P3).
  if (isLoading) return <LoadingState label="Loading your brands…" />;
  if (isLoadingError) {
    return (
      <div className="mx-auto max-w-2xl">
        <LoadErrorState
          title="Your brands could not be loaded"
          description="We could not check which brands you can access. Try again."
          onRetry={refetch}
        />
      </div>
    );
  }

  return (
    <Card className="mx-auto max-w-2xl rounded-[20px] border border-amber-200 bg-amber-50 p-6 shadow-sm">
      <p className="font-data text-[11px] font-medium uppercase tracking-[0.16em] text-amber-700">
        Operator Brand Required
      </p>
      <h1 className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-slate-950">
        {title}
      </h1>
      <p className="mt-3 text-sm leading-6 text-slate-700">
        {description}
      </p>
      <div className="mt-5 rounded-2xl border border-amber-200 bg-white p-4">
        {hasMultipleBrands ? (
          <label htmlFor="operator-brand-gate-select" className="block">
            <span className="sr-only">Select brand</span>
            <select
              id="operator-brand-gate-select"
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm transition-colors focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
              value={activeBrandId ?? ""}
              onChange={(event) => {
                if (!event.target.value) return;
                activateBrand(event.target.value);
              }}
            >
              <option value="" disabled>Select a brand</option>
              {brands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brandSelectionLabel(brand, brands)}
                </option>
              ))}
            </select>
          </label>
        ) : brands[0] ? (
          <button
            type="button"
            onClick={() => activateBrand(brands[0]!.id)}
            className="inline-flex rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Continue with {brands[0]!.name}
          </button>
        ) : (
          <p className="text-sm text-slate-600">
            No accessible brands were found for this workspace.
          </p>
        )}
      </div>
    </Card>
  );
}
